// src/scenes/BaseMenuScene.js
// v8 - Uses SceneManager via startScene helper, refined resize/shutdown

import MenuUIUtils from "../ui/MenuUIUtils.js";
import CanvasFixer from "../canvasFixer.js";
import Phaser from 'phaser';
import sceneManager from "../utils/SceneManager.js"; // Import SceneManager

class BaseMenuScene extends Phaser.Scene {
  constructor(config) {
    super(config);
    this.menuItems = [];
    this.uiElements = {};
    this.baseY = 200;
    this.itemSpacing = 60;
    this.minSpacing = 40;
    this.maxSpacing = 70;
    this.centered = false;
    this.menuOffsetX = 0.1;
    this.padding = 20;
    this.vSpacing = 15;
    this.boundHandleResize = this.handleResize.bind(this);
    this.backgroundElements = {};
    this.backgroundOptions = {
      useAITheme: true,
      backgroundColor: "#050010",
      gridColor: 0x00AA77,
      codeColor: 0x00DDAA,
      gridAlpha: 0.08,
      codeAlpha: 0.07,
      particleCount: 20,
      depth: -10,
    };
    this.tiles = [];
    this.isTransitioning = false; // Local flag (SceneManager's is primary)
    this.headerButtons = {}; // Store header buttons created by helper
  }

  init(data) {
    console.debug(`Initializing ${this.scene.key}`);
    this.menuItems = [];
    this.uiElements = {};
    this.backgroundElements = {};
    this.tiles = [];
    this.headerButtons = {};
    this.passedData = data || {};
    this.isTransitioning = false; // Reset flag on init
    
    // Initialize UI readiness flag for SceneManager transition timing
    this.isUIReady = false;

    if (typeof CanvasFixer !== 'undefined' && CanvasFixer.fixSceneCanvas) {
      CanvasFixer.fixSceneCanvas(this);
    }
  }

  create() {
    console.debug(`BaseMenuScene create for: ${this.scene.key}`);
    try {
      if (this.cameras?.main) this.cameras.main.setBackgroundColor("#000000");

      this.setRibbonVisibility(true);
      this.createBackground();
      this.inputLocked = false;

      this.scale.on('resize', this.boundHandleResize);
      this.events.on('shutdown', this.shutdown, this);

      // REMOVED: this.time.delayedCall(20, () => this.handleResize());
      // This was the cause of the race condition. The child scene is now responsible
      // for calling handleResize() after it has finished creating its own elements.

      if (typeof CanvasFixer !== 'undefined' && CanvasFixer.fixCanvas) CanvasFixer.fixCanvas();
      
      // Signal that base scene setup is complete - child scenes should override this
      // and call it after their specific UI creation is done
      this.isUIReady = true;
      console.debug(`[${this.scene.key}] BaseMenuScene setup complete, ready for fade-in`);

    } catch (error) { this.handleSceneError(error); }
  }

  createBackground() {
    if (this.backgroundElements && Object.keys(this.backgroundElements).length > 0) {
      if (this.backgroundOptions.useAITheme) {
        MenuUIUtils.cleanupAIThemedBackground(this.backgroundElements);
      } else {
        MenuUIUtils.cleanupProfessionalBackground(this.backgroundElements);
      }
      this.backgroundElements = {};
    }
    if (this.backgroundOptions.useAITheme) {
      this.backgroundElements = MenuUIUtils.createAIThemedBackground(this, this.backgroundOptions);
    } else {
      this.backgroundElements = MenuUIUtils.createProfessionalBackground(this, this.backgroundOptions);
    }
    
    // UNIFIED: Use centralized mobile detection
    import('../services/MobileDetectionService.js').then(({ MobileDetectionService }) => {
      if (MobileDetectionService.isMobile()) {
        this.enableMobileScrolling();
        console.log('📱 Mobile scrolling enabled for', this.scene.key);
      }
    });
    
    return this.backgroundElements;
  }
  
  enableMobileScrolling() {
    // REMOVED: Conflicting mobile scrolling system
    // Mobile scrolling is now handled by UnifiedMobileSystem
    console.log('📜 Mobile scrolling delegated to UnifiedMobileSystem');
  }

  calculateIdealSpacing() {
    try {
      const itemCount = this.menuItems?.length || 1;
      if (itemCount <= 0 || !this.cameras?.main?.height) return this.itemSpacing;
      const availableHeight = this.cameras.main.height - this.baseY - 100;
      const idealSpacing = availableHeight / Math.max(itemCount, 1);
      const currentMaxSpacing = (this.cameras.main.width < 600) ? 50 : this.maxSpacing;
      this.itemSpacing = Phaser.Math.Clamp(idealSpacing, this.minSpacing, currentMaxSpacing);
      return this.itemSpacing;
    } catch (error) { console.warn("Error calculateIdealSpacing:", error); return this.itemSpacing; }
  }

  handleResize(gameSize) {
    if (!this.cameras?.main || !this.children || !this.scene?.systems?.scenePlugin?.isActive(this.scene.key)) return;

    const width = this.cameras.main.width;
    const height = this.cameras.main.height;
    const centerX = width / 2;

    this.calculateIdealSpacing();
    const baseY = this.baseY || height * 0.3;
    const itemSpacing = this.itemSpacing || 60;
    const targetX = this.centered ? centerX : width * this.menuOffsetX;

    // Reposition general menu items
    this.menuItems.forEach((item, index) => {
      if (item && item.active && typeof item.setPosition === 'function') {
        item.setPosition(targetX, baseY + (index * itemSpacing));
      }
    });

    // Reposition specific UI elements (like title, footer)
    if (this.uiElements.title && this.uiElements.title.active) {
      this.uiElements.title.setPosition(centerX, 80);
    }
    const footerY = height - 25;
    const footerSpacing = 100;
    if (this.uiElements.emailText) this.uiElements.emailText.setPosition(centerX - footerSpacing, footerY);
    if (this.uiElements.termsText) this.uiElements.termsText.setPosition(centerX, footerY);
    if (this.uiElements.privacyText) this.uiElements.privacyText.setPosition(centerX + footerSpacing, footerY);

    // Reposition tiles (if they exist) - assumes grid layout logic is handled by subclass or helper
    // This part might need adjustment based on how tiles are positioned (e.g., if using createGameTileGrid)
    if (this.tiles && this.tiles.length > 0) {
        // Example: If using createGameTileGrid, it might need to be called again here,
        // or the tiles need individual repositioning based on the grid logic.
        // For simplicity, let's assume a helper function exists or is called in subclass handleResize
        // this.repositionTileGrid(); // Placeholder for grid repositioning logic
    }


    // Update background
    if (this.backgroundElements && Object.keys(this.backgroundElements).length > 0) {
        this.createBackground(); // Recreate for simplicity
    } else {
        this.createBackground();
    }
  }

  clearMenuItems() {
    this.menuItems.forEach(item => { if (item?.active) try { item.destroy(); } catch(e){} });
    this.menuItems = [];
  }

  handleSceneError(error) {
    console.error(`Error in ${this.scene.key}:`, error);
    try {
      if(this.children) this.children.removeAll(true); // Destroy children
      if(this.tweens) this.tweens.killAll();
      if(this.time) this.time.removeAllEvents();
      this.cameras.main.setBackgroundColor("#330000");
      this.add.text(
        this.cameras.main.width / 2, this.cameras.main.height / 2,
        "Scene Load Error.\nPlease Refresh.",
        { fontFamily: 'monospace', fontSize: '24px', color: '#FF8888', align: 'center' }
      ).setOrigin(0.5);
    } catch (e) { console.error("Error displaying scene error message:", e); }
  }

  // --- Scene Transition Helper ---
  startScene(targetScene, data = {}) {
    const currentKey = this.scene.key;
    console.log(`[${currentKey}] Requesting transition to ${targetScene} via SceneManager`);

    if (!this.input.enabled) {
        console.warn(`[${currentKey}] Input disabled, cannot start scene ${targetScene}.`);
        // Optionally show a message
        // this.showMessage("Please wait...", 1000, true);
        return;
    }
    this.isTransitioning = false; // Reset local flag just in case

    try {
        sceneManager.switchToScene(targetScene, data);
    } catch (error) {
        console.warn(`[${currentKey}] SceneManager transition request for ${targetScene} failed:`, error.message);
        // SceneManager's error handling should take over, but we can show a fallback message
        // this.showMessage("Navigation Error.", 2000, true);
    }
  }

  // --- Delegated UI Helpers ---
  createThemedText(x, y, text, sizeKey = 'sizeMd', colorHex = MenuUIUtils.COLORS.textLight, origin = 0.5) {
    return MenuUIUtils.createThemedText(this, x, y, text, sizeKey, colorHex, origin);
  }

  createStyledButton(x, y, text, callback, color, width = 180, height = 40, fontSizeKey = 'sizeMd') {
    const button = MenuUIUtils.createStyledButton(this, x, y, text, callback, {
        bgColor: color, width: width, height: height,
        fontSize: MenuUIUtils.FONTS[fontSizeKey] || MenuUIUtils.FONTS.sizeMd
    });
    if (button) {
        const key = `button_${text.replace(/\s+/g, '')}_${Date.now() % 1000}`;
        this.uiElements[key] = button; // Store for potential cleanup/access
        if (button.input && !button.input.enabled) { button.setInteractive({ useHandCursor: true }); }
    }
    return button;
  }

  createMenuItem(x, y, text, callback, options = {}) {
     const menuItem = MenuUIUtils.createMenuItem(this, x, y, text, callback, options);
     if (menuItem) { this.menuItems.push(menuItem); }
     return menuItem;
  }
  
  createTile(title, x, y, width, height, bgColor, soloCallback, teamCallback, options = {}) { // Added options parameter
    // Delegate to the MenuUIUtils function, passing all arguments including options
    const tile = MenuUIUtils.createGameTile(this, title, x, y, width, height, bgColor, soloCallback, teamCallback, options);
    if (tile) {
        this.tiles.push(tile); // Store for resize/cleanup
    }
    return tile;
  }
  createUtilityTile(title, x, y, width, height, color, callback) {
    const tile = MenuUIUtils.createUtilityTile(this, title, x, y, width, height, color, callback);
    if (tile) { this.tiles.push(tile); } // Store for resize/cleanup
    return tile;
  }

  // --- REPLACE THIS ENTIRE METHOD ---
  createGameTileGrid(startY, tileData, options = {}) {
    // Clear existing tiles array before creating new ones
    this.tiles.forEach(tile => { if (tile?.active) try { tile.destroy(); } catch(e){} });
    this.tiles = [];
    console.log(`[${this.scene.key}] Creating tile grid...`); // Add log

    const width = this.cameras.main.width;
    const height = this.cameras.main.height;
    // Default configuration for the grid layout
    const defaults = {
        maxTileWidth: 280,
        tileHeight: 130,
        horizontalGap: 20,
        verticalGap: 20,
        bottomPadding: 50,
        forceColumns: null
    };
    const config = { ...defaults, ...options }; // Merge user options

    // UNIFIED: Use centralized mobile detection
    import('../services/MobileDetectionService.js').then(({ MobileDetectionService }) => {
      const isMobile = MobileDetectionService.isMobile();
    });
    const numCols = config.forceColumns || (isMobile ? 1 : (width < 900 ? 2 : 3)); // 1 col mobile, 2 col tablet, 3 col desktop
    const availableWidth = width - (this.padding * 2); // Usable width within scene padding
    const tileWidth = Math.min(config.maxTileWidth, (availableWidth - (config.horizontalGap * (numCols - 1))) / numCols);
    const totalGridWidth = (tileWidth * numCols) + (config.horizontalGap * (numCols - 1));
    const startX = (width - totalGridWidth) / 2 + (tileWidth / 2); // Center the grid horizontally

    // Calculate vertical spacing, adjusting if content exceeds available height
    const totalRows = Math.ceil(tileData.length / numCols);
    const totalGridHeight = (config.tileHeight * totalRows) + (config.verticalGap * (totalRows - 1));
    const availableHeight = height - startY - config.bottomPadding;
    const adjustedVerticalGap = totalGridHeight > availableHeight
        ? Math.max(10, (availableHeight - (config.tileHeight * totalRows)) / (Math.max(1, totalRows - 1))) // Ensure denominator isn't zero
        : config.verticalGap;

    // Iterate through tileData and create tiles
    tileData.forEach((data, index) => {
        const col = index % numCols;
        const row = Math.floor(index / numCols);
        const tileX = startX + (col * (tileWidth + config.horizontalGap));
        const tileY = startY + (row * (config.tileHeight + adjustedVerticalGap)) + (config.tileHeight / 2);

        let tile;
        // --- **THE FIX IS HERE** ---
        // Pass the entire 'data' object from tileData as the 'options'
        // parameter to the tile creation functions. This ensures flags like
        // 'teamComingSoon', 'teamDisabled', 'teamComingSoonMessage' etc. are received.
        if (data.utility) {
            // Create a utility tile (e.g., Profile, Hall of Fame)
             console.log(`[${this.scene.key}] Creating Utility Tile: ${data.title} with options`, data);
            tile = this.createUtilityTile(data.title, tileX, tileY, tileWidth, config.tileHeight, data.color, data.utility);
            // Note: createUtilityTile doesn't use teamComingSoon, but passing 'data' is harmless.
        } else {
            // Create a standard game tile with Solo/Team buttons
            console.log(`[${this.scene.key}] Creating Game Tile: ${data.title} with options`, data);
            tile = this.createTile(data.title, tileX, tileY, tileWidth, config.tileHeight, data.color, data.solo, data.team, data); // <-- Pass 'data' as options
        }
        // --- **END FIX** ---

        // createTile/createUtilityTile already adds to this.tiles if successful
        if (!tile) {
            console.error(`[${this.scene.key}] Failed to create tile for:`, data.title);
        }
    });

    console.log(`[${this.scene.key}] Tile grid creation finished. ${this.tiles.length} tiles created.`);
    return this.tiles; // Return the array of created tile containers
  }
  // --- END REPLACEMENT ---

  createHeaderButtons(topY, leftButtons = [], rightButtons = [], options = {}) {
    // Clear existing header buttons before creating new ones
    Object.values(this.headerButtons).forEach(btn => { if (btn?.active) try { btn.destroy(); } catch(e){} });
    this.headerButtons = {};

    const width = this.cameras.main.width;
    const defaults = { buttonSpacing: 10, textButtons: false };
    const config = { ...defaults, ...options };

    let leftX = this.padding;
    leftButtons.forEach((btnConfig, index) => {
        const buttonWidth = btnConfig.width || 100;
        const buttonKey = btnConfig.key || `leftBtn${index}`;
        const buttonX = leftX + (buttonWidth / 2);
        let button;
        if (config.textButtons) { button = this.createMenuItem(buttonX, topY, btnConfig.text, btnConfig.callback, { fill: btnConfig.color, size: btnConfig.size, originX: 0.5, originY: 0.5 }); }
        else { button = this.createStyledButton(buttonX, topY, btnConfig.text, btnConfig.callback, btnConfig.color, buttonWidth, btnConfig.height || 35, btnConfig.size || 'sizeSm'); }
        this.headerButtons[buttonKey] = button;
        leftX += buttonWidth + config.buttonSpacing;
    });

    let rightX = width - this.padding;
    // Iterate backwards for right alignment positioning
    for (let i = rightButtons.length - 1; i >= 0; i--) {
        const btnConfig = rightButtons[i];
        const buttonWidth = btnConfig.width || (btnConfig.isLabel ? (btnConfig.text.length * 10) : 100); // Estimate label width
        const buttonKey = btnConfig.key || `rightBtn${i}`;
        const buttonX = rightX - (buttonWidth / 2);
        let button;

        if (btnConfig.isLabel) {
            button = this.createThemedText(rightX, topY, btnConfig.text, btnConfig.size || 'sizeMd', btnConfig.color || MenuUIUtils.COLORS.primary, 1); // Origin 1 for right align
            this.uiElements[buttonKey] = button; // Store labels in uiElements
        } else if (config.textButtons) {
            button = this.createMenuItem(buttonX, topY, btnConfig.text, btnConfig.callback, { fill: btnConfig.color, size: btnConfig.size, originX: 0.5, originY: 0.5 });
        } else {
            button = this.createStyledButton(buttonX, topY, btnConfig.text, btnConfig.callback, btnConfig.color, buttonWidth, btnConfig.height || 35, btnConfig.size || 'sizeSm');
        }
        this.headerButtons[buttonKey] = button; // Store button ref
        rightX -= (buttonWidth + config.buttonSpacing);
    }
    return this.headerButtons;
  }


  // --- Button State Helpers ---
  setButtonLoading(buttonContainer, loadingText, disable = true) {
    return MenuUIUtils.setButtonLoading(buttonContainer, loadingText, disable);
  }

  resetButtonState(buttonContainer, defaultText, defaultColor = null) {
    return MenuUIUtils.resetButtonState(buttonContainer, defaultText, defaultColor);
  }

  showMessage(message, duration = 2000, isError = false) {
    return MenuUIUtils.showMessage(this, message, duration, isError);
  }

  setRibbonVisibility(visible = true) {
    const topRibbon = document.getElementById("top-ribbon");
    if (topRibbon) { topRibbon.style.display = visible ? 'flex' : 'none'; }
    const gameContainer = document.getElementById("game-container");
    if (gameContainer) {
      const ribbonHeight = visible ? 50 : 0;
      gameContainer.style.top = `${ribbonHeight}px`;
      gameContainer.style.height = `calc(100% - ${ribbonHeight}px)`;
    }
  }

  // --- Shutdown ---
  shutdown() {
    const sceneKey = this.scene?.key || 'BaseMenuScene';
    console.debug(`[${sceneKey}] Shutting down BaseMenuScene resources...`);

    try { this.scale?.off('resize', this.boundHandleResize, this); } catch(e){}
    try { this.events?.off('shutdown', this.shutdown, this); } catch(e){}
    try { this.time?.removeAllEvents(); } catch(e){}
    try { this.tweens?.killAll(); } catch(e){}

    if (this.backgroundElements && Object.keys(this.backgroundElements).length > 0) {
        try {
            if (this.backgroundOptions?.useAITheme) { MenuUIUtils.cleanupAIThemedBackground(this.backgroundElements); }
            else { MenuUIUtils.cleanupProfessionalBackground(this.backgroundElements); }
        } catch(e) { console.error(`[${sceneKey}] Error cleaning background:`, e); }
        this.backgroundElements = {};
    }

    const safeDestroy = (obj, key = 'unknown') => {
        if (!obj || !obj.scene || obj.scene !== this || obj.active === false) return false;
        if (typeof obj.destroy === 'function') {
            try {
                if (obj.type === 'Container' && obj.list) { [...obj.list].forEach((child, index) => safeDestroy(child, `${key}_child_${index}`)); }
                obj.destroy(true); return true;
            } catch (e) { if (!e.message.toLowerCase().includes('destroyed')) { console.error(`   ERROR destroying ${key} (Type: ${obj.type}):`, e); } }
        } return false;
    };

    if (this.tiles && this.tiles.length > 0) { this.tiles.forEach((tile, index) => safeDestroy(tile, `tile_${index}`)); this.tiles = []; }
    if (this.headerButtons) { Object.entries(this.headerButtons).forEach(([key, button]) => { safeDestroy(button, `headerBtn_${key}`); }); this.headerButtons = {}; }
    if (this.uiElements && Object.keys(this.uiElements).length > 0) { Object.entries(this.uiElements).forEach(([key, element]) => { safeDestroy(element, `uiElement_${key}`); }); this.uiElements = {}; }
    if (this.menuItems && this.menuItems.length > 0) { this.clearMenuItems(); } else { this.menuItems = []; }

    console.debug(`BaseMenuScene Shutdown completed for ${sceneKey}`);
  }
}

export default BaseMenuScene;